import { Button, Card, Form, Input, Modal } from "antd";
import { toast } from "react-toastify";
import { postApi } from "../../services/api";
import { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";

const paymentVersions = {
	trial: {
		title: "Trial Version",
		price: "10",
		duration: "7 Days",
		features: "Limited access to basic modules.",
	},
	full: {
		title: "Full Version",
		price: "100",
		duration: "Monthly Access",
		features:
			"Unlimited access to all modules, certifications, and premium support.",
	},
};

const PaymentOptions = ({
	isModalVisible,
	setModalVisible,
	course,
	selectedClassroom,
	user,
}) => {
	const [cbeSelected, setCbeSelected] = useState(false);
	const [stripeSelected, setStripeSelected] = useState(false);
	const [paymentVersion, setPaymentVersion] = useState(
		paymentVersions.trial.title
	);
	const [paymentType, setPaymentType] = useState(null);

	const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

	// Handle Enroll Button Click
	const handleEnroll = async (values) => {
		if (paymentType === "cbe" && cbeSelected) {
			const response = await postApi("/payment/cbe", {
				classroomId: selectedClassroom,
				userId: user._id,
				amount: course.price,
				status: "incomplete",
				transactionNo: values?.transactionNo,
			});
			if (response.error) {
				toast.error("There was an error:", response.message);
			}
			setCbeSelected(false);
			setModalVisible(false);
			toast.success("Payment submitted successfully");
			return;
		}
		try {
			const isTrial = paymentVersion === paymentVersions.trial.title;
			const payload = {
				courseName: course.name,
				coursePrice: isTrial
					? paymentVersions.trial.price
					: course.price,
				userId: user._id,
				courseId: course._id,
				classroomId: selectedClassroom,
				status: isTrial ? "trial" : "completed",
			};

			const response = await postApi(
				"/stripe/create-checkout-session",
				payload
			);

			if (response && response.sessionId) {
				const stripe = await stripePromise;
				const { error } = await stripe.redirectToCheckout({
					sessionId: response.sessionId,
				});

				if (error) {
					console.error("Stripe Checkout Error:", error.message);
					toast.error(
						"An error occurred while redirecting to payment."
					);
				}
			} else {
				toast.error(
					response?.error || "Failed to initiate payment session."
				);
			}
		} catch (error) {
			console.error("Enrollment Error:", error.message);
			toast.error("Error enrolling into course. Please try again.");
		}
	};

	return (
		<>
			<Modal
				open={isModalVisible}
				onCancel={() => setModalVisible(false)}
				onOk={() => {
					if (paymentType === "cbe") {
						setCbeSelected(true);
						return;
					} else if (paymentType === "stripe") {
						// handleEnroll();
						setStripeSelected(true);
					} else {
						toast.error("You must select a payment gateway");
					}
				}}
			>
				<div className="flex gap-8 p-5 m-5">
					<div
						className={`rounded-xl border-2 ${
							paymentType === "stripe"
								? " border-blue-400 bg-gray-50 "
								: "border-gray-300"
						} p-4 hover:border-blue-400 `}
						onClick={() => setPaymentType("stripe")}
					>
						<img src="/images/stripe.png" alt="Stripe" />
					</div>
					<div
						className={`rounded-xl border-2 ${
							paymentType === "cbe"
								? "  border-blue-400 bg-gray-50"
								: "border-gray-300"
						} p-4 hover:border-blue-400 `}
						onClick={() => setPaymentType("cbe")}
					>
						<img src="/images/CBE.svg" alt="CBE" />
					</div>
				</div>
			</Modal>
			<Modal
				open={cbeSelected}
				onCancel={() => setCbeSelected(false)}
				width={800}
				footer={null}
			>
				<div className="grid grid-cols-2 gap-8 w-full h-full justify-center items-center p-4 m-1">
					<div className="relative top-5 p-6">
						<img src="/images/CBE.svg" alt="CBE logo" />
					</div>
					<Form
						layout="vertical"
						className="w-full"
						style={{ paddingBottom: 0 }}
						size="large"
						onFinish={handleEnroll}
					>
						<Form.Item
							label="Transaction Number"
							name={"transactionNo"}
						>
							<Input placeholder="xxx-xxxx-xxxx" />
						</Form.Item>
						<Form.Item>
							<Button type="primary" htmlType="submit">
								Submit
							</Button>
						</Form.Item>
					</Form>
				</div>
			</Modal>
			<Modal
				open={stripeSelected}
				onCancel={() => setStripeSelected(false)}
				width={800}
				footer={null}
				className="pb-2"
			>
				<div className="flex flex-col w-full h-full justify-center items-center p-4 m-1">
					<img
						src="/images/stripe.png"
						alt="Stripe logo"
						width={100}
					/>
					<Form
						layout="vertical"
						className="w-full flex flex-col items-end"
						size="large"
						onFinish={handleEnroll}
					>
						<div className="grid grid-cols-2 gap-4 w-full justify-center">
							<CourseVersion
								details={paymentVersions.trial}
								selected={
									paymentVersion ===
									paymentVersions.trial.title
								}
								handleClick={() =>
									setPaymentVersion(
										paymentVersions.trial.title
									)
								}
							/>
							<CourseVersion
								details={{
									...paymentVersions.full,
									price: course.price,
								}}
								selected={
									paymentVersion ===
									paymentVersions.full.title
								}
								handleClick={() =>
									setPaymentVersion(
										paymentVersions.full.title
									)
								}
							/>
						</div>
						<Form.Item className="mt-8">
							<Button type="primary" htmlType="submit">
								Submit
							</Button>
						</Form.Item>
					</Form>
				</div>
			</Modal>
		</>
	);
};

const CourseVersion = ({ details, selected, handleClick }) => {
	const { title, price, duration, features } = details;
	return (
		<Card
			title={title}
			className={`mt-2 pb-10 border ${
				selected ? "border-mainColor bg-gray-50" : "border-gray-200"
			} cursor-pointer`}
			onClick={handleClick}
		>
			<p>
				<strong>Price:</strong> ${price}
			</p>
			<p>
				<strong>Duration:</strong> {duration}
			</p>
			<p>
				<strong>Features:</strong> {features}
			</p>
		</Card>
	);
};

export default PaymentOptions;
